import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loader from 'components/loader/Loader';
import Tooltip from 'components/tooltip/Tooltip';
import useBillablePropertiesCount from 'components/domain/propertiesList/useBillablePropertiesCount';
import { PropertiesFilterSchemaType } from '../filters/PropertiesFilter.schema';
import PropertiesContext from '../PropertiesContext';
import PropertyListItem from '../item/PropertyListItem';
import useFetchPropertiesForPropertiesBody from './useFetchPropertiesForPropertiesBody';
import useSearchFieldFilters from './useSearchFieldFilters';
import PropertiesBodyListPlaceholder from './PropertiesBodyListPlaceholder';
import {
  PropertiesBodyListStyled,
  PropertiesCountContainer,
} from './PropertiesBody.styles';

const PropertiesBody = () => {
  const { t } = useTranslation();
  const { defaultValues } = useFormState<PropertiesFilterSchemaType>();
  const { lastSubmittedFilters } = useContext(PropertiesContext);
  const { searchFieldFilters } = useSearchFieldFilters();
  const filters = lastSubmittedFilters ?? defaultValues;
  const { data, isLoading, fetchNextPage, hasNextPage, totalFilteredCount } =
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    useFetchPropertiesForPropertiesBody({ ...filters, ...searchFieldFilters });
  const { calculateAmountProperties } = useBillablePropertiesCount();

  if (isLoading) {
    return <PropertiesBodyListPlaceholder />;
  }

  const loadedProperties = data?.pages?.flatMap(({ properties }) => properties);
  const loadedPropertiesCount = calculateAmountProperties(
    loadedProperties ?? [],
  );

  return (
    <>
      <PropertiesCountContainer>
        <Tooltip
          text={t('pageProperties.body.billablePropertiesOnly')}
          id="billable-properties-only"
        >
          <div>
            {t('pageProperties.body.showingAmountProperties', {
              showingAmount: loadedPropertiesCount,
              totalAmount: totalFilteredCount,
            })}
          </div>
        </Tooltip>
      </PropertiesCountContainer>
      <PropertiesBodyListStyled>
        <InfiniteScroll
          dataLength={loadedProperties?.length ?? 0}
          next={fetchNextPage}
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          hasMore={hasNextPage && !!loadedProperties?.length}
          loader={<Loader />}
          scrollableTarget="app-page-content"
          endMessage={
            !loadedProperties?.length && (
              <span>{t('pageProperties.body.noPropertyFound')}</span>
            )
          }
        >
          {loadedProperties?.map((property) => (
            <PropertyListItem key={property.uid} property={property} />
          ))}
        </InfiniteScroll>
      </PropertiesBodyListStyled>
    </>
  );
};

export default PropertiesBody;
